import React, { FunctionComponent, useEffect } from "react";
import styles from "./TextVideo.module.scss";
import TextVideoProps from "./TextVideoProps.interface";

import { motion, useAnimation } from "framer-motion";
import { useDispatch } from "react-redux";
import { useInView } from "react-intersection-observer";
import Anchor from "components/Anchor/Anchor";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getVideoAspectRatio from "app/media/helpers/getVideoAspectRatio";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import handleOpenModal from "blocks/helpers/handleOpenModal";
import Paragraph from "components/Paragraph/Paragraph";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import VideoViewer from "components/VideoViewer/VideoViewer";
import Button from "components/Button/Button";
import isBrowserContext from "helpers/isBrowserContext";

const TextVideo: FunctionComponent<TextVideoProps> = (props) => {
    const dispatch = useDispatch();

    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const videoAspectRatio = getVideoAspectRatio(props.settings.videoCrop, "Widescreen (16:9)");
    const videoPositionClass = props.settings.videoPosition === "Media Right" ? ` ${styles.has_video_right}` : undefined;

    const isAutoplay = props.settings.videoAutoplay ? props.settings.videoAutoplay : false;
    const isLoop = props.settings.videoLoop ? props.settings.videoLoop : false;
    const showControls = !isAutoplay;

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={combineClasses([styles.container, videoPositionClass])}>
                <div className={styles.media}>
                    {/* Media */}
                    {props.content.video && props.content.video[0] && props.content.video[0].content.video?.umbracoFile ? (
                        <VideoViewer src={props.content.video[0].content.video?.umbracoFile} alt={props.content.videoAlternative} autoplay={isAutoplay} loop={isLoop} controls={showControls} aspectRatioX={videoAspectRatio.aspectRatioX} aspectRatioY={videoAspectRatio.aspectRatioY} />
                    ) : null}

                    {/* Video Embed */}
                    {props.content.video && props.content.video[0] && props.content.video[0].content.videoUrl ? (
                        <VideoEmbed src={props.content.video[0].content.videoUrl} alt={props.content.videoAlternative} autoplay={isAutoplay} loop={isLoop} aspectRatioX={16} aspectRatioY={9} />
                    ) : null}

                    {/* Caption */}
                    {props.content.videoCaption && props.content.videoCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.videoCaption}</div>
                    ) : null}
                </div>

                <div className={combineClasses([styles.content])}>
                    {/* Tagline */}
                    {props.content.tagline ? (
                        <span className={styles.tagline}>{props.content.tagline}</span>
                    ) : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading className={styles.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight}>{props.content.headline}</DynamicHeading>
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <Button key={button.contentUdi} className={styles.button} modal={button.content.modal} onClick={() => { button.content.modal ? handleOpenModal(button.content.modal, button.content.stickyLink, dispatch) : undefined; }} to={button.content.link} parameters={button.content.parameters} anchor={button.content.anchor} settings={button.settings}>
                                        {button.content.label}
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default TextVideo;